import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  IconButton,
  alpha,
  useTheme,
  Fade,
  Stack,
} from "@mui/material";
import { 
  Close, 
  AssignmentTurnedIn, 
  ArrowForwardIos,
  AutoAwesome
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { activateCarePlan, getCarePlans } from "../../../../api/services/carePathWay";
import { QK } from "../../../../base/qk";

const CarePlanPickerDialog = ({ open, onClose, clientID, onSelect }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  const { data = [], isLoading } = useQuery(
    [QK.inactiveCarePlans, clientID],
    () => getCarePlans({ status: "inactive", client: clientID }),
    { enabled: open }
  );

  const { mutate, isLoading: activating, variables } = useMutation(
    (planId) => activateCarePlan({ client: clientID, form: planId }),
    {
      onSuccess: (_, planId) => {
        queryClient.invalidateQueries(QK.activeCarePlans);
        const selectedPlan = data.find(plan => plan.id === planId);
        onSelect(planId, selectedPlan?.title);
        onClose();
      },
    }
  );

  const handleSelect = (planId) => {
    if (!activating) {
      mutate(planId);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: { borderRadius: "24px", p: 1, backgroundImage: "none" }
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ 
              p: 1, 
              borderRadius: "12px", 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              display: "flex"
            }}>
              <AutoAwesome fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: "-0.5px" }}>
                Activate Care Plan
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select a pathway to begin patient care
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: "#f5f5f5" }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 4 }}>
        {isLoading ? (
          <Box textAlign="center" py={8}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
              Loading available plans...
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box textAlign="center" py={6} sx={{ bgcolor: "#f8fafc", borderRadius: "20px" }}>
            <AssignmentTurnedIn sx={{ fontSize: 48, color: "text.disabled", mb: 1, opacity: 0.5 }} />
            <Typography color="text.secondary" fontWeight={600}>
              No care plans found
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {data.map((plan, index) => (
              <Fade in={true} style={{ transitionDelay: `${index * 50}ms` }} key={plan.id}>
                <Card
                  onClick={() => handleSelect(plan.id)}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "20px",
                    cursor: activating && variables === plan.id ? "default" : "pointer",
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    bgcolor: activating && variables === plan.id ? alpha(theme.palette.primary.main, 0.04) : "transparent",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                      borderColor: theme.palette.primary.main,
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                      {plan.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {plan.description || "No additional description available for this pathway."}
                    </Typography>
                  </Box>

                  <Box sx={{ ml: 2 }}>
                    {activating && variables === plan.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: "10px", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        }
                      }}>
                        <ArrowForwardIos sx={{ fontSize: 12 }} />
                      </Box>
                    )}
                  </Box>
                </Card>
              </Fade>
            ))}
          </Stack>
        )}
      </DialogContent>

      <Box sx={{ px: 3, pb: 3 }}>
        <Button
          fullWidth
          onClick={onClose}
          sx={{ 
            py: 1.5, 
            borderRadius: "16px", 
            textTransform: "none", 
            fontWeight: 700,
            color: "text.secondary",
            bgcolor: "#f1f5f9",
            "&:hover": { bgcolor: "#e2e8f0" }
          }}
        >
          Cancel and Go Back
        </Button>
      </Box>
    </Dialog>
  );
};

export default CarePlanPickerDialog;